import React, { useEffect, useState } from 'react';
import { getHistory, deleteSession, getSessionAudio, toggleSessionFavorite } from '../services/storageService';
import { SessionRecord } from '../types';
import { IntegrityStatus } from '../contracts/scoring';

export const SessionHistory: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onLoadSession: (session: SessionRecord) => void;
}> = ({ isOpen, onClose, onLoadSession }) => {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    const data = await getHistory();
    data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setSessions(data);
    setLoading(false);
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (confirm('Supprimer définitivement cette session ?')) {
      await deleteSession(id);
      loadData();
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, session: SessionRecord) => {
    e.stopPropagation();
    await toggleSessionFavorite(session.id);
    setSessions(prev => prev.map(s => s.id === session.id ? { ...s, isFavorite: !s.isFavorite } : s));
  };

  const handleSelect = async (session: SessionRecord) => {
    if (session.audioExpired) {
      alert('Fichier audio expiré. Seul le rapport est disponible.');
    }

    setLoadingId(session.id);

    if (!session.stuAudioBlob && !session.audioExpired) {
      try {
        const blob = await getSessionAudio(session.id);
        if (blob) session.stuAudioBlob = blob;
      } catch (e) {
        console.error('Failed to load audio', e);
      }
    }

    onLoadSession(session);
    setLoadingId(null);
  };

  if (!isOpen) return null;

  return (
    <div className="w-full h-full flex flex-col animate-fade-in">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        {loading && (
          <div className="text-center py-20 text-slate-500 font-black uppercase text-[10px] tracking-widest italic animate-pulse">
            Chargement de la base studio…
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="text-center py-20 text-slate-500 flex flex-col items-center gap-6 opacity-30">
            <span className="text-6xl">🕸️</span>
            <p className="font-black uppercase text-[10px] tracking-[0.4em]">
              Aucune session enregistrée
            </p>
          </div>
        )}

        {!loading && sessions.length > 0 && (
          <div className="space-y-4">
            {sessions.map((session, index) => {
              const prev = sessions[index + 1];
              const trend =
                prev && session.score !== null && prev.score !== null ? session.score - prev.score : 0;

              const integrityStatus: IntegrityStatus =
                (session.result?.session?.truth_report?.integrity_status as IntegrityStatus) ?? 'UNVERIFIABLE';

              const isPass = integrityStatus === 'PASS';

              // ✅ show score if PASS; otherwise hide
              const displayScore = isPass ? session.score : null;

              const isLoadingThis = loadingId === session.id;

              // optional “cloud certified” badge if remote_ai_status OK
              const remoteStatus = session.result?.audit_trail?.remote_ai_status;
              const isCloudCertified = isPass && remoteStatus === 'OK';

              return (
                <div
                  key={session.id}
                  onClick={() => !isLoadingThis && handleSelect(session)}
                  className={`group p-6 bg-white/5 border rounded-[32px] cursor-pointer transition-all flex items-center justify-between
                    ${session.isFavorite ? 'border-smart-gold/40' : 'border-white/5'}
                    ${isLoadingThis ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <div className="flex gap-6 items-center">
                    <div className="relative">
                      <div className="w-14 h-14 rounded-2xl bg-black/40 flex items-center justify-center text-xl border border-white/5">
                        {isLoadingThis ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : '🎸'}
                      </div>

                      <button
                        onClick={(e) => handleToggleFavorite(e, session)}
                        className={`absolute -top-2 -left-2 w-6 h-6 rounded-full text-xs flex items-center justify-center
                          ${session.isFavorite ? 'bg-smart-gold text-black' : 'bg-white/10 text-slate-500 opacity-0 group-hover:opacity-100'}`}
                      >
                        ★
                      </button>
                    </div>

                    <div>
                      <h3 className="font-black text-white text-lg italic uppercase mb-1 truncate max-w-[200px]">
                        {session.title || 'Session sans titre'}
                      </h3>

                      <div className="flex items-center gap-3 text-[9px] uppercase tracking-widest">
                        <span className="text-slate-500">{new Date(session.date).toLocaleDateString()}</span>
                        <span className="text-smart-gold font-black">{session.bpm} BPM</span>

                        {isCloudCertified && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-smart-gold/10 border border-smart-gold/30 text-smart-gold font-black">
                            🛡️ CERTIFIED
                          </span>
                        )}

                        {isPass && !isCloudCertified && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 font-black">
                            ✓ VERIFIED (LOCAL)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-2xl font-black italic ${displayScore !== null && displayScore >= 80 ? 'text-emerald-400' : 'text-smart-gold'}`}>
                          {displayScore !== null ? displayScore : '—'}
                        </span>
                        <span className="text-[10px] font-black text-slate-600">/100</span>
                      </div>

                      {trend !== 0 && displayScore !== null && (
                        <div className={`text-[8px] font-black uppercase flex justify-end gap-1 ${trend > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {trend > 0 ? '▲' : '▼'} {Math.abs(trend)} pts
                        </div>
                      )}
                    </div>

                    <button
                      onClick={(e) => handleDelete(e, session.id)}
                      className="w-10 h-10 rounded-full bg-white/5 hover:bg-red-500/20 text-slate-600 hover:text-red-400 transition-all opacity-0 group-hover:opacity-100"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
